import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type WallKey = "marchi-propri" | "marchi-difesa" | "progetti";
export type GapSize = "sm" | "md" | "lg";

export interface HomeWallSettings {
  id?: string;
  wall_key: WallKey;
  cols_mobile: number;
  cols_tablet: number;
  cols_desktop: number;
  cols_wide: number;
  gap_size: GapSize;
  max_items: number | null;
  is_visible: boolean;
}

const DEFAULTS: Record<WallKey, HomeWallSettings> = {
  "marchi-propri": { wall_key: "marchi-propri", cols_mobile: 2, cols_tablet: 3, cols_desktop: 4, cols_wide: 5, gap_size: "md", max_items: null, is_visible: true },
  "marchi-difesa": { wall_key: "marchi-difesa", cols_mobile: 2, cols_tablet: 3, cols_desktop: 4, cols_wide: 5, gap_size: "md", max_items: null, is_visible: true },
  "progetti": { wall_key: "progetti", cols_mobile: 2, cols_tablet: 3, cols_desktop: 4, cols_wide: 5, gap_size: "md", max_items: null, is_visible: true },
};

export const useHomeWallSettings = (wallKey: WallKey) => {
  return useQuery({
    queryKey: ["home-wall", wallKey],
    queryFn: async (): Promise<HomeWallSettings> => {
      const { data, error } = await supabase
        .from("home_wall_settings")
        .select("*")
        .eq("wall_key", wallKey)
        .maybeSingle();
      if (error || !data) return DEFAULTS[wallKey];
      return data as HomeWallSettings;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAllHomeWallSettings = () => {
  return useQuery({
    queryKey: ["home-wall", "all"],
    queryFn: async (): Promise<HomeWallSettings[]> => {
      const { data, error } = await supabase
        .from("home_wall_settings")
        .select("*")
        .order("wall_key");
      if (error || !data) return Object.values(DEFAULTS);
      return data as HomeWallSettings[];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateHomeWallSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (s: HomeWallSettings) => {
      const { data, error } = await supabase
        .from("home_wall_settings")
        .upsert(
          {
            wall_key: s.wall_key,
            cols_mobile: s.cols_mobile,
            cols_tablet: s.cols_tablet,
            cols_desktop: s.cols_desktop,
            cols_wide: s.cols_wide,
            gap_size: s.gap_size,
            max_items: s.max_items,
            is_visible: s.is_visible,
          },
          { onConflict: "wall_key" }
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["home-wall", vars.wall_key] });
      qc.invalidateQueries({ queryKey: ["home-wall", "all"] });
    },
  });
};

// Static class maps so Tailwind JIT picks them up
const COLS_MOBILE: Record<number, string> = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" };
const COLS_SM: Record<number, string> = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4", 5: "sm:grid-cols-5" };
const COLS_MD: Record<number, string> = { 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5", 6: "md:grid-cols-6" };
const COLS_LG: Record<number, string> = { 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6", 7: "lg:grid-cols-7", 8: "lg:grid-cols-8" };
const GAP: Record<GapSize, string> = {
  sm: "gap-2 sm:gap-3 md:gap-4",
  md: "gap-3 sm:gap-4 md:gap-6",
  lg: "gap-4 sm:gap-6 md:gap-8",
};

export const buildWallGridClasses = (s: HomeWallSettings): string => {
  const cls = [
    "grid",
    COLS_MOBILE[s.cols_mobile] || "grid-cols-2",
    COLS_SM[s.cols_tablet] || "sm:grid-cols-3",
    COLS_MD[s.cols_desktop] || "md:grid-cols-4",
    COLS_LG[s.cols_wide] || "lg:grid-cols-5",
    GAP[s.gap_size] || GAP.md,
    "mx-auto max-w-6xl",
  ];
  return cls.join(" ");
};

export const wallDefaults = DEFAULTS;
